import { useCallback, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";

import { EmptyState } from "@/components/shared/EmptyState";
import { LoadingPage } from "@/components/shared/LoadingPage";
import { PageHeader } from "@/components/shared/PageHeader";
import { Card, CardContent } from "@/components/ui/card";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabs";
import { getEnterprise } from "@/services/platformEnterprisesService";

import { EnterpriseDetailsTab } from "./enterprise-detail-tabs/enterprise-details-tab";
import { EnterpriseMembersTab } from "./enterprise-detail-tabs/enterprise-members-tab";
import { EnterpriseProductsTab } from "./enterprise-detail-tabs/enterprise-products-tab";

const UUID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

const TABS = ["details", "members", "products"] as const;
type TabValue = (typeof TABS)[number];

function isValidTab(value: string | null): value is TabValue {
  return TABS.includes(value as TabValue);
}

export function EnterpriseDetailPage(): JSX.Element {
  const { enterpriseId } = useParams<{ enterpriseId: string }>();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const idValid = Boolean(enterpriseId && UUID_RE.test(enterpriseId));

  const rawTab = searchParams.get("tab");
  const [activeTab, setActiveTab] = useState<TabValue>(
    isValidTab(rawTab) ? rawTab : "details",
  );

  const handleTabChange = useCallback(
    (value: string): void => {
      const tab = value as TabValue;
      setActiveTab(tab);
      setSearchParams(tab === "details" ? {} : { tab }, { replace: true });
    },
    [setSearchParams],
  );

  const QUERY_KEY = ["platform", "enterprises", "detail", enterpriseId] as const;

  const enterpriseQuery = useQuery({
    queryKey: QUERY_KEY,
    queryFn: () => getEnterprise(enterpriseId!),
    enabled: idValid,
  });

  if (!idValid) {
    return (
      <div className="p-4 md:p-8">
        <PageHeader
          title={t("platformEnterprises.editPageTitle")}
          onBack={() => navigate("/platform/enterprises")}
        />
        <Card className="mt-4 shadow-sm">
          <CardContent className="flex min-h-[40vh] items-center justify-center py-12">
            <EmptyState
              title={t("platformEnterprises.notFoundTitle")}
              message={t("platformEnterprises.notFoundMessage")}
            />
          </CardContent>
        </Card>
      </div>
    );
  }

  if (enterpriseQuery.isLoading) {
    return <LoadingPage />;
  }

  if (enterpriseQuery.isError || !enterpriseQuery.data) {
    return (
      <div className="p-4 md:p-8">
        <PageHeader
          title={t("platformEnterprises.editPageTitle")}
          onBack={() => navigate("/platform/enterprises")}
        />
        <Card className="mt-4 shadow-sm">
          <CardContent className="flex min-h-[40vh] items-center justify-center py-12">
            <EmptyState
              title={t("platformEnterprises.notFoundTitle")}
              message={t("platformEnterprises.notFoundMessage")}
            />
          </CardContent>
        </Card>
      </div>
    );
  }

  const enterprise = enterpriseQuery.data;

  return (
    <div className="p-4 md:p-8">
      <PageHeader
        title={enterprise.name}
        onBack={() => navigate("/platform/enterprises")}
      />

      <Tabs
        value={activeTab}
        onValueChange={handleTabChange}
        className="mt-4"
      >
        <TabsList>
          <TabsTrigger value="details">
            {t("platformEnterprises.tabDetails")}
          </TabsTrigger>
          <TabsTrigger value="members">
            {t("platformEnterprises.tabMembers")}
          </TabsTrigger>
          <TabsTrigger value="products">
            {t("platformEnterprises.tabProducts")}
          </TabsTrigger>
        </TabsList>

        <TabsContent value="details">
          <EnterpriseDetailsTab
            enterprise={enterprise}
            enterpriseId={enterpriseId!}
          />
        </TabsContent>

        <TabsContent value="members">
          <EnterpriseMembersTab enterpriseId={enterpriseId!} />
        </TabsContent>

        <TabsContent value="products">
          <EnterpriseProductsTab enterpriseId={enterpriseId!} />
        </TabsContent>
      </Tabs>
    </div>
  );
}
